import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { AdsDetailApi } from 'src/modules/ads/detail/api'
import { globalOptions } from 'src/core/constants/globalOptions'
import { IMaskInput } from 'react-imask'
import { rationalNumbers } from 'Core/masks'
import {
  getCurrentAd,
  getRegistrationFormulas,
  getRegistrationPremiumClasses,
  getRegistrationFormulaDetails
} from 'src/modules/ads/detail/selectors'
import {
  fetchRegistrationFormulas,
  fetchRegistrationPremiumClasses,
  fetchRegistrationFormulaDetails
} from 'src/modules/ads/detail/actions'
import { fetchCategory } from 'src/modules/categories/actions'
import { getCurrentSubCategory } from 'src/modules/categories/selectors'
import { fetchCitiesByCountry } from 'src/modules/geoObjects/cities/actions'
import { getCities, getCitiesLoading } from 'src/modules/geoObjects/cities/selectors'
import config from 'SRC/config/config.json'

class Registration extends React.Component {
  constructor (props) {
    super(props)

    this.api = new AdsDetailApi()
    this.state = this.initialState()
  }

  initialState = () => ({
    currentFormula: null,
    currentPremiumClass: null,

    cost: 0,

    additionalPayments: [],

    insuranceSpecVal: null,
    insuranceSpecError: false,
    insuranceSpecTouched: false,

    taxesSpecVal: null,
    taxesSpecError: false,
    taxesSpecTouched: false,

    yearSpecVal: null,

    showTaxesSpec: true,

    city: null,

    newPlates: 0
  })

  async componentDidMount () {
    await this.props.fetchRegistrationFormulas()
    this.props.fetchRegistrationPremiumClasses()
    this.props.fetchCitiesByCountry(globalOptions.MontenegroId)

    if (this.props.currentAd) {
      await this.props.fetchCategory({ seo: this.props.currentAd.category.seo }, false)
      if (this.props.category && this.props.category.registrationFormulas.length) {
        const currentFormulaId = this.props.category.registrationFormulas[0].formula.id
        this.updateCurrentFormula(currentFormulaId)
      }
    } else {
      const { registrationFormulas } = this.props
      if (registrationFormulas && Array.isArray(registrationFormulas) && registrationFormulas.length) {
        this.updateCurrentFormula(registrationFormulas[0].id)
      }
    }
  }

  updateCurrentFormula = async formulaId => {
    await this.props.fetchRegistrationFormulaDetails(formulaId)
    await this.setState({
      insuranceSpecError: false,
      insuranceSpecTouched: false,
      taxesSpecError: false,
      taxesSpecTouched: false
    })

    // get specification values of current ad
    const formulaDetails = this.props.registrationFormula
    const currentAd = this.props.currentAd
    let insuranceSpecVal = null
    let taxesSpecVal = null
    let yearSpecVal = null
    let showTaxesSpec = true

    if (currentAd && currentAd.adsSpecifications && currentAd.adsSpecifications.length) {
      if (formulaDetails) {
        // get Insurance Specification value for current Ad
        if (formulaDetails.registrationInsuranceSpecification.id) {
          const currentAdInsuranceSpec = currentAd.adsSpecifications.find(adSpec =>
            adSpec.specification.id === formulaDetails.registrationInsuranceSpecification.id)
          if (currentAdInsuranceSpec && currentAdInsuranceSpec.value) {
            insuranceSpecVal = currentAdInsuranceSpec.value
          }
        }

        // get Taxes Specification value for current Ad
        if (formulaDetails.registrationTaxesSpecification.id) {
          const currentAdTaxesSpec = currentAd.adsSpecifications.find(adSpec =>
            adSpec.specification.id === formulaDetails.registrationTaxesSpecification.id)
          if (currentAdTaxesSpec && currentAdTaxesSpec.value) {
            taxesSpecVal = currentAdTaxesSpec.value
          }
        }

        // get Year Specification value for current Ad
        if (formulaDetails.registrationYearSpecification.id) {
          const currentAdYearSpec = currentAd.adsSpecifications.find(adSpec =>
            adSpec.specification.id === formulaDetails.registrationYearSpecification.id)
          if (currentAdYearSpec && currentAdYearSpec.value) {
            yearSpecVal = currentAdYearSpec.value
          }
        }
      }
    }

    if (formulaDetails) {
      const insuranceSpec = formulaDetails.registrationInsuranceSpecification
      const taxesSpec = formulaDetails.registrationTaxesSpecification
      if (insuranceSpec) {
        if (taxesSpec && taxesSpec.id === insuranceSpec.id) {
          showTaxesSpec = false
        }
      }
    }

    // get city of current ad
    let city = null
    if (currentAd && currentAd.city && currentAd.city.id) city = currentAd.city.id

    this.setState({
      currentFormula: formulaId,
      currentPremiumClass: this.props.registrationPremiumClasses.find(pClass =>
        pClass.name === 'PR7').id,
      insuranceSpecVal,
      taxesSpecVal,
      yearSpecVal,
      showTaxesSpec,
      city,
      newPlates: 0
    })
  }

  onFormulaChange = e => this.updateCurrentFormula(e.target.value)

  onPremiumClassChange = e => this.setState({ currentPremiumClass: e.target.value })

  onAdditionalPaymentChecked = e => {
    let additionalPayments = [...this.state.additionalPayments]
    if (additionalPayments.includes(e.target.value)) {
      additionalPayments = additionalPayments.filter(ap => ap !== e.target.value)
    } else {
      additionalPayments.push(e.target.value)
    }

    this.setState({ additionalPayments })
  }

  onInsuranceSpecValChange = value => {
    let newState = {
      insuranceSpecVal: value,
      insuranceSpecTouched: true,
      insuranceSpecError: this.state.insuranceSpecTouched && !(value && value > 0)
    }

    if (!this.state.showTaxesSpec) {
      newState = {
        ...newState,
        taxesSpecVal: value,
        taxesSpecTouched: true,
        taxesSpecError: this.state.taxesSpecTouched && !(value && value > 0)
      }
    }
    this.setState(newState)
  }

  onTaxesSpecValChange = value => {
    const newState = {
      taxesSpecVal: value,
      taxesSpecTouched: true,
      taxesSpecError: this.state.taxesSpecTouched && !(value && value > 0)
    }
    this.setState(newState)
  }

  onYearSpecValChange = value => this.setState({ yearSpecVal: value })

  onCityChange = e => this.setState({ city: e.target.value })

  onNewPlatesChange = e => this.setState({ newPlates: e.target.value })

  calculateCost = async () => {
    const {
      currentFormula,
      currentPremiumClass,
      additionalPayments,
      insuranceSpecVal,
      taxesSpecVal,
      yearSpecVal,
      city,
      newPlates
    } = this.state

    await this.setState({
      insuranceSpecError: !(insuranceSpecVal && insuranceSpecVal > 0),
      taxesSpecError: !(taxesSpecVal && taxesSpecVal > 0),
      insuranceSpecTouched: true,
      taxesSpecTouched: true
    })

    const params = {
      formula: currentFormula,
      premiumClass: currentPremiumClass,
      additionalPayments: additionalPayments.map(ap => Number(ap)),
      insuranceSpecVal: `${insuranceSpecVal}`,
      taxesSpecVal: `${taxesSpecVal}`,
      yearSpecVal: Number(yearSpecVal) ? yearSpecVal : null,
      city: Number(city) ? city : null,
      newPlates: newPlates === '1'
    }
    console.log(params)
    const result = this.state.insuranceSpecError || this.state.taxesSpecError ? 0
      : await this.api.calculateRegistrationCost(params)
    this.setState({ cost: result })
  }

  renderRegistrationAdditionalPayment = () => {
    const additionalPayment = this.props.registrationFormula.registrationAdditionalPayment
    if (additionalPayment && additionalPayment.length) {
      return <div className='form-group'>
        <label>Dodatna plaćanja</label>
        {additionalPayment.map(payment => <div key={payment.id}>
          <input type='checkbox' value={payment.id} onClick={this.onAdditionalPaymentChecked} />
          <label style={{display: 'inline-block'}} key={`label${payment.id}`}>{payment.name}</label>
        </div>)}
      </div>
    } else {
      return null
    }
  }

  renderRegistrationInsuranceSpecification = () => {
    const insuranceSpecification = this.props.registrationFormula.registrationInsuranceSpecification
    if (insuranceSpecification && Object.keys(insuranceSpecification).length) {
      const inputType = insuranceSpecification.type
      const title = `${insuranceSpecification.title}${insuranceSpecification.measure
        ? ` (${insuranceSpecification.measure})` : ''}`
      const { insuranceSpecError, insuranceSpecTouched } = this.state
      switch (inputType) {
        case 'input': return (
          <div className={`form-group ${insuranceSpecError && insuranceSpecTouched ? 'form-group_error' : ''}`}>
            <label>{title}</label>
            <IMaskInput
              {...rationalNumbers}
              onAccept={this.onInsuranceSpecValChange}
              value={this.state.insuranceSpecVal}
              type='text'
            />
          </div>
        )
        case 'select': return (
          <div className={`form-group ${insuranceSpecError && insuranceSpecTouched ? 'form-group_error' : ''}`}>
            <label>{title}</label>
            <select value={this.state.insuranceSpecVal} onChange={(e) => this.onInsuranceSpecValChange(e.target.value)}>
              <option value={0}>Izaberi</option>
              {insuranceSpecification.options.map(option => (<option value={option}>
                {option}
              </option>))}
            </select>
          </div>
        )
        default: return null
      }
    }
  }

  renderRegistrationTaxesSpecification = () => {
    const taxesSpecification = this.props.registrationFormula.registrationTaxesSpecification
    if (taxesSpecification && Object.keys(taxesSpecification).length) {
      const inputType = taxesSpecification.type
      const title = `${taxesSpecification.title}${taxesSpecification.measure
        ? ` (${taxesSpecification.measure})` : ''}`
      const { taxesSpecError, taxesSpecTouched } = this.state
      switch (inputType) {
        case 'input': return (
          <div className={`form-group ${taxesSpecError && taxesSpecTouched ? 'form-group_error' : ''}`}>
            <label>{title}</label>
            <IMaskInput
              {...rationalNumbers}
              onAccept={this.onTaxesSpecValChange}
              value={this.state.taxesSpecVal}
              type='text'
            />
          </div>
        )
        case 'select': return (
          <div className={`form-group ${taxesSpecError && taxesSpecTouched ? 'form-group_error' : ''}`}>
            <label>{title}</label>
            <select value={this.state.taxesSpecVal} onChange={(e) => this.onTaxesSpecValChange(e.target.value)}>
              <option value={0}>Izaberi</option>
              {taxesSpecification.options.map(option => (<option value={option}>
                {option}
              </option>))}
            </select>
          </div>
        )
        default: return null
      }
    }
  }

  renderRegistrationYearSpecification = () => {
    const yearSpecification = this.props.registrationFormula.registrationYearSpecification
    if (yearSpecification && Object.keys(yearSpecification).length) {
      const inputType = yearSpecification.type
      switch (inputType) {
        case 'input': return (
          <div className='form-group'>
            <label>{yearSpecification.title}</label>
            <IMaskInput
              {...rationalNumbers}
              onAccept={this.onYearSpecValChange}
              value={this.state.yearSpecVal}
              type='text'
            />
          </div>
        )
        case 'select': return (
          <div className='form-group'>
            <label>{yearSpecification.title}</label>
            <select value={this.state.yearSpecVal} onChange={(e) => this.onYearSpecValChange(e.target.value)}>
              <option value={0}>Izaberi</option>
              {yearSpecification.options.map(option => (<option value={option}>
                {option}
              </option>))}
            </select>
          </div>
        )
        default: return null
      }
    }
  }

  render = () => (
    <div className='oglasi-tab-content-registracija'>
      <div className={`vehicle-registration${this.props.isForApp ? '__app' : ''}`}>
        <div className='form-group'>
          <label>Tip vozila</label>
          <select id='tip-vozila' key='tip-vozila' onChange={this.onFormulaChange}
            value={this.state.currentFormula ? this.state.currentFormula : 1}>
            {this.props.registrationFormulas.map(formula =>
              <option key={formula.id} value={formula.id}>
                {formula.name}
              </option>
            )}
          </select>
        </div>

        <div className='form-group'>
          <label>Premijski razred</label>
          <select id='premijski-razred' key='premijski-razred' onChange={this.onPremiumClassChange}
            value={this.state.currentPremiumClass ? this.state.currentPremiumClass : 1}>
            {this.props.registrationPremiumClasses.map(premiumClass =>
              <option key={premiumClass.id} value={premiumClass.id}>
                {premiumClass.name}
              </option>
            )}
          </select>
        </div>

        {this.props.registrationFormula ? [
          this.renderRegistrationAdditionalPayment(),
          this.renderRegistrationInsuranceSpecification(),
          this.state.showTaxesSpec ? this.renderRegistrationTaxesSpecification() : null,
          this.renderRegistrationYearSpecification()
        ] : null}

        {!this.props.isCitiesLoading ? <div className='form-group'>
          <label>Grad</label>
          <select id='grad' key='grad' onChange={this.onCityChange} value={this.state.city}>
            <option value={0}>Izaberi</option>
            {this.props.cities.map(city =>
              <option key={city.value} value={city.value}>{city.label}</option>
            )}
          </select>
        </div> : null}

        <div className='form-group'>
          <label>Nove tablice</label>
          <select id='nove-tablice' key='nove-tablice' onChange={this.onNewPlatesChange}
            value={this.state.newPlates}>

            <option key={1} value={1}>Da</option>
            <option key={0} value={0}>Ne</option>
          </select>
        </div>
      </div>
      <button className='form-submit-btn' onClick={this.calculateCost}>Izračunaj</button>
      <h1>Ukupno: {this.state.cost.toFixed(2)}€</h1>

      <div className='vehicle-registration-ad-slot-wrapper' style={{ textAlign: 'center' }}>
        <img src='/img' />
      </div>
    </div>
  )
}

const mapStateToProps = state => {
  return {
    currentAd: getCurrentAd(state),
    category: getCurrentSubCategory(state),
    registrationFormulas: getRegistrationFormulas(state),
    registrationFormula: getRegistrationFormulaDetails(state),
    registrationPremiumClasses: getRegistrationPremiumClasses(state),
    cities: getCities(state),
    isCitiesLoading: getCitiesLoading(state)
  }
}

Registration.propTypes = {
  currentAd: PropTypes.shape({
    category: PropTypes.object,
    adsSpecifications: PropTypes.object,
    city: PropTypes.object
  }),
  category: PropTypes.shape({
    registrationFormulas: PropTypes.array
  }),
  registrationFormulas: PropTypes.array.isRequired,
  registrationFormula: PropTypes.object,
  registrationPremiumClasses: PropTypes.array.isRequired,
  cities: PropTypes.array.isRequired,
  isCitiesLoading: PropTypes.bool.isRequired,
  fetchCategory: PropTypes.func,
  fetchRegistrationFormulas: PropTypes.func.isRequired,
  fetchRegistrationPremiumClasses: PropTypes.func.isRequired,
  fetchRegistrationFormulaDetails: PropTypes.func.isRequired,
  fetchCitiesByCountry: PropTypes.func.isRequired,
  isForApp: PropTypes.bool
}

export default connect(mapStateToProps, {
  fetchCategory,
  fetchRegistrationFormulas,
  fetchRegistrationPremiumClasses,
  fetchRegistrationFormulaDetails,
  fetchCitiesByCountry
})(Registration)
